import { useState, useEffect, useCallback } from "react";
import {
  Form, Input, Select, Switch, Button, Space, message, Row, Col, Divider, Typography,
} from "antd";
import { PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import DataTable from "../../../components/common/DataTable";
import FilterBar from "../../../components/common/FilterBar";
import StatusBadge from "../../../components/common/StatusBadge";
import PageLoader from "../../../components/common/PageLoader";
import ConfirmDeleteModal from "../../../components/common/ConfirmDeleteModal";
import usePermission from "../../../hooks/usePermission";
import useSettingsDropdown from "../../../hooks/useSettingsDropdown";
import { getUser } from "../../../utils/storage";
import { canDeleteAdminUser, getDeleteDisabledReason } from "./userRules";
import {
  listAdminUsers,
  createAdminUser,
  updateAdminUserProfile,
  deleteAdminUser,
} from "../../../services/adminUsers.service";

const { Text } = Typography;

const VIEW_LIST = "list";
const VIEW_FORM = "form";

export default function BasicInfoTab() {
  const [form] = Form.useForm();
  const { canEdit, canDelete } = usePermission("usersLeaves");
  const currentUser = getUser();

  // View state
  const [view, setView] = useState(VIEW_LIST);
  const [editingUser, setEditingUser] = useState(null); // null = add mode, object = edit mode

  // List state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // Settings dropdowns
  const { options: designationOptions } = useSettingsDropdown("designations");
  const { options: projectOptions } = useSettingsDropdown("projects");
  const { options: cityOptions } = useSettingsDropdown("city-zones", {
    filter: (item) => item.entityType === "city",
  });
  const { options: zoneOptions } = useSettingsDropdown("city-zones", {
    filter: (item) => item.entityType === "zone",
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listAdminUsers({ page, per_page: pageSize, search });
      setUsers(result.records || []);
      setTotalCount(result.pagination?.total_count || 0);
    } catch {
      message.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ─── List Actions ─────────────────────────────────
  const handleAddNew = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      isCodNonApprover: false,
      emailNotifications: true,
      role: "L2",
    });
    setView(VIEW_FORM);
  };

  const handleEdit = (record) => {
    setEditingUser(record);
    form.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      email: record.email,
      designation: record.designation,
      primaryContact: record.primaryContact,
      emergencyContact: record.emergencyContact,
      project: record.project,
      city: record.city,
      zone: record.zone,
      role: record.role,
      isActive: record.isActive,
      isCodNonApprover: record.isCodNonApprover,
      emailNotifications: record.emailNotifications,
    });
    setView(VIEW_FORM);
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete User",
      content: `Are you sure you want to delete "${record.firstName} ${record.lastName}"?`,
      onConfirm: async () => {
        try {
          await deleteAdminUser(record._id);
          message.success("User deleted");
          fetchUsers();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Delete failed");
        }
      },
    });
  };

  const handleBackToList = () => {
    setView(VIEW_LIST);
    setEditingUser(null);
    form.resetFields();
  };

  // ─── Form Actions ─────────────────────────────────
  const handleReset = () => {
    if (editingUser) {
      form.setFieldsValue(editingUser);
    } else {
      form.resetFields();
      form.setFieldsValue({
        isActive: true,
        isCodNonApprover: false,
        emailNotifications: true,
        role: "L2",
      });
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (editingUser) {
        await updateAdminUserProfile(editingUser._id, values);
        message.success("User updated successfully");
      } else {
        await createAdminUser(values);
        message.success("User created successfully. Welcome email sent.");
      }
      await fetchUsers();
      handleBackToList();
    } catch (err) {
      if (err?.errors?.length) {
        message.error(err.errors[0]?.msg || "Save failed");
      } else {
        message.error(err?.message || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const isEdit = !!editingUser;

  // ─── List View ────────────────────────────────────
  if (view === VIEW_LIST) {
    const columns = [
      {
        title: "Name",
        key: "name",
        sorter: true,
        render: (_, record) => (
          <Text strong style={{ fontSize: 13 }}>
            {record.firstName} {record.lastName}
          </Text>
        ),
      },
      { title: "Email", dataIndex: "email", sorter: true },
      { title: "Role", dataIndex: "role", sorter: true },
      {
        title: "Status",
        dataIndex: "status",
        render: (status) => <StatusBadge status={status} />,
      },
      {
        title: "Last Login",
        dataIndex: "lastLogin",
        render: (val) => val ? new Date(val).toLocaleDateString("en-GB") : "Never",
      },
    ];

    if (loading && users.length === 0) return <PageLoader tip="Loading users..." />;

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <Text strong style={{ fontSize: 16 }}>Basic Info</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>Create or edit admin user details</Text>
          </div>
          {canEdit && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
              Add User
            </Button>
          )}
        </div>

        <FilterBar
          searchPlaceholder="Search by name or email..."
          onSearch={(val) => { setSearch(val); setPage(1); }}
          filters={[
            {
              key: "role",
              label: "Role",
              options: [
                { label: "L1 — Super Admin", value: "L1" },
                { label: "L2 — Operations Admin", value: "L2" },
                { label: "L3 — Finance Admin", value: "L3" },
              ],
            },
          ]}
        />

        <DataTable
          columns={columns}
          dataSource={users}
          loading={loading}
          pagination={{ current: page, pageSize, total: totalCount }}
          onPageChange={(p) => setPage(p)}
          actions={{
            onEdit: canEdit ? handleEdit : null,
            onDelete: canDelete ? handleDelete : null,
            canEdit,
            canDelete,
            isDeleteDisabled: (record) => !canDeleteAdminUser(record, currentUser),
            getDeleteDisabledReason: (record) => getDeleteDisabledReason(record, currentUser),
          }}
        />
      </div>
    );
  }

  // ─── Form View ────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>{isEdit ? "Edit User" : "Add New User"}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {isEdit ? `Editing: ${editingUser.firstName} ${editingUser.lastName}` : "Create a new admin user"}
          </Text>
        </div>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBackToList}>
            Back to Listing
          </Button>
          <Button onClick={handleReset}>Reset</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            {isEdit ? "Save Changes" : "Create User"}
          </Button>
        </Space>
      </div>

      {/* Form Card */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: 24 }}>
        <Form form={form} layout="vertical" requiredMark={false}>

          {/* Account Section (only for new users) */}
          {!isEdit && (
            <>
              <Text style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b" }}>
                Account
              </Text>
              <Divider style={{ margin: "8px 0 16px" }} />
              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item name="role" label="Role" rules={[{ required: true }]}>
                    <Select options={[
                      { label: "L1 — Super Admin", value: "L1" },
                      { label: "L2 — Operations Admin", value: "L2" },
                      { label: "L3 — Finance Admin", value: "L3" },
                    ]} />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <div style={{ 
                    background: "#f8fafc", 
                    border: "1px solid #e2e8f0", 
                    borderRadius: 4, 
                    padding: "12px 16px",
                    marginTop: 30,
                  }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      <strong>Note:</strong> A secure random password will be automatically generated and sent to the user's email address. 
                      User will be required to change password on first login.
                    </Text>
                  </div>
                </Col>
              </Row>
            </>
          )}

          {/* Personal Details */}
          <Text style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b" }}>
            Personal Details
          </Text>
          <Divider style={{ margin: "8px 0 16px" }} />

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="firstName" label="First Name" rules={[{ required: true, message: "First name is required" }]}>
                <Input placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: "Last name is required" }]}>
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="email" label="Email Address" rules={[{ required: true, message: "Email is required" }, { type: "email", message: "Enter a valid email" }]}>
                <Input placeholder="Enter email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="designation" label="Designation">
                <Select placeholder="Select designation" options={designationOptions} allowClear />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="primaryContact" label="Primary Contact">
                <Input addonBefore="+968" placeholder="9402 1122" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="emergencyContact" label="Emergency Contact">
                <Input addonBefore="+968" placeholder="9505 4433" />
              </Form.Item>
            </Col>
          </Row>

          {/* Assignment */}
          <Text style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginTop: 8, display: "block" }}>
            Assignment
          </Text>
          <Divider style={{ margin: "8px 0 16px" }} />

          <Row gutter={24}>
            <Col span={8}>
              <Form.Item name="project" label="Project">
                <Select placeholder="Select project" options={projectOptions} allowClear />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="city" label="City">
                <Select placeholder="Select city" options={cityOptions} allowClear />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="zone" label="Zone">
                <Select placeholder="Select zone" options={zoneOptions} allowClear />
              </Form.Item>
            </Col>
          </Row>

          {/* Toggles */}
          <div style={{ display: "flex", gap: 48, paddingTop: 8, borderTop: "1px solid #f5f5f5", marginTop: 8, paddingBottom: 8 }}>
            <Form.Item name="isActive" valuePropName="checked" style={{ marginBottom: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Switch />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>Active Status</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>User can access the platform</div>
                </div>
              </div>
            </Form.Item>
            <Form.Item name="isCodNonApprover" valuePropName="checked" style={{ marginBottom: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Switch />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>COD Non-Approver</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>Restrict COD approval rights</div>
                </div>
              </div>
            </Form.Item>
            <Form.Item name="emailNotifications" valuePropName="checked" style={{ marginBottom: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Switch />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>Email Notifications</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>Receive email alerts on key events</div>
                </div>
              </div>
            </Form.Item>
          </div>
        </Form>

        {/* Footer */}
        {isEdit && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Last modified: {editingUser.updatedAt ? new Date(editingUser.updatedAt).toLocaleString() : "—"}
            </Text>
            <Space>
              <Button onClick={handleReset}>Reset Defaults</Button>
              <Button type="primary" onClick={handleSave} loading={saving}>Save & Close</Button>
            </Space>
          </div>
        )}
      </div>
    </div>
  );
}
